import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { FormEvent, MouseEvent } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, X } from 'lucide-react'
import { inlineMarkdownFrom, inlineToHtml } from '@/lib/review-inline-markdown'
import { placeReviewChips } from '@/lib/review-chip-place'
import { testIds } from '@/lib/test-ids'
import {
  hunkIds,
  markdownFromPresentation,
  presentBlock,
  remainingProposal,
  reviewBlocks,
  savedMarkdown,
  type EditBlock,
  type HunkDecision,
} from '@/lib/block-edit-review'
import '@/components/features/document-reading-prose.css'
import '@/components/prototype/maater-ui-diff-review-scene.css'

const SETTLE_MS = 560
const WASH_OUT_MS = 720

export type DocumentEditDecision = {
  kind: 'accepted' | 'rejected'
  saved: string
  proposed: string
  pending: number
}

type DocumentEditReviewProps = {
  title: string
  original: string
  proposed: string
  busy?: boolean
  /** Bumps when the file row is clicked so the current edit scrolls into view. */
  scrollNonce?: number
  onDecide: (decision: DocumentEditDecision) => Promise<boolean>
  onClose: () => void
}

function chunkBlocks(blocks: EditBlock[]): { id: string; hunkId?: string; blocks: EditBlock[] }[] {
  const chunks: { id: string; hunkId?: string; blocks: EditBlock[] }[] = []
  for (const block of blocks) {
    const prev = chunks[chunks.length - 1]
    const hunkId = block.hunkId && block.kind !== 'same' ? block.hunkId : undefined
    if (hunkId && prev?.hunkId === hunkId) {
      prev.blocks.push(block)
      continue
    }
    chunks.push({ id: block.id, hunkId, blocks: [block] })
  }
  return chunks
}

function readMarkdown(el: HTMLElement, raw: string): string {
  const presented = presentBlock(raw)
  if (presented.node === 'ul') {
    const items = [...el.querySelectorAll('li')]
      .map((item) => inlineMarkdownFrom(item).trim())
      .filter((item) => item.length > 0)
    return markdownFromPresentation({ node: 'ul', items }, raw)
  }
  return markdownFromPresentation({ ...presented, text: inlineMarkdownFrom(el) }, raw)
}

function BlockBody({
  block,
  editable,
  onFocus,
  onBlur,
  onInput,
}: {
  block: EditBlock
  editable: boolean
  onFocus?: () => void
  onBlur?: () => void
  onInput?: (markdown: string) => void
}) {
  const presented = presentBlock(block.text)
  const edit = editable
    ? {
        contentEditable: true as const,
        suppressContentEditableWarning: true,
        className: 'outline-none',
        onMouseDown: onFocus,
        onFocus,
        onBlur,
        onInput: (event: FormEvent<HTMLElement>) => {
          onInput?.(readMarkdown(event.currentTarget, block.text))
        },
        onClick: (event: MouseEvent<HTMLElement>) => {
          if (!(event.target instanceof HTMLAnchorElement)) return
          if (event.metaKey || event.ctrlKey) return
          event.preventDefault()
        },
      }
    : undefined
  const html = (value: string) => ({ __html: inlineToHtml(value) })
  if (presented.node === 'h1') {
    return <h1 {...edit} dangerouslySetInnerHTML={html(presented.text ?? '')} />
  }
  if (presented.node === 'h2') {
    return <h2 {...edit} dangerouslySetInnerHTML={html(presented.text ?? '')} />
  }
  if (presented.node === 'ul') {
    return (
      <ul {...edit}>
        {(presented.items ?? []).map((item, index) => (
          <li key={`${block.id}-${index}`} dangerouslySetInnerHTML={html(item)} />
        ))}
      </ul>
    )
  }
  return <p {...edit} dangerouslySetInnerHTML={html(presented.text ?? '')} />
}

export function DocumentEditReview({
  title,
  original,
  proposed,
  busy = false,
  scrollNonce = 0,
  onDecide,
  onClose,
}: DocumentEditReviewProps) {
  const blocks = reviewBlocks(original, proposed)
  const hunks = hunkIds(blocks)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const overrides = useRef<Record<string, string>>({})
  const settleTimer = useRef<number | null>(null)
  const washTimer = useRef<number | null>(null)
  const freezeScroll = useRef(false)
  const holdScroll = useRef(false)
  const heldTop = useRef(0)
  const userScrolledReview = useRef(false)
  const scrollPin = useRef<{ anchorId: string; anchorTop: number; scrollTop: number } | null>(null)
  const [decisions, setDecisions] = useState<Record<string, HunkDecision>>({})
  const [settling, setSettling] = useState<{ hunkId?: string; kind: 'accepted' | 'rejected' } | null>(null)
  const [washOut, setWashOut] = useState<string[]>([])
  const [editingHunk, setEditingHunk] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [away, setAway] = useState<'above' | 'below' | null>(null)
  const [nextChangeEdge, setNextChangeEdge] = useState<'above' | 'below' | null>(null)
  const [nextChangeIndex, setNextChangeIndex] = useState(0)
  const [hunkCursor, setHunkCursor] = useState(0)

  const sourceKey = `${original}\0${proposed}`
  const sourceRef = useRef(sourceKey)
  if (sourceRef.current !== sourceKey) {
    sourceRef.current = sourceKey
    overrides.current = {}
    if (Object.keys(decisions).length > 0) setDecisions({})
    if (washOut.length > 0) setWashOut([])
    if (editingHunk) setEditingHunk(null)
  }

  const pending = hunks.filter((id) => (decisions[id] ?? 'pending') === 'pending')
  const cursor = pending.length === 0 ? 0 : Math.min(hunkCursor, pending.length - 1)
  const activeHunk = pending[cursor] ?? null
  const locked = busy || saving || settling != null

  const scrollToActive = (behavior: ScrollBehavior) => {
    const scroller = scrollerRef.current
    const target = scroller?.querySelector('[data-diff-scroll-target="true"]')
    if (!scroller || !(target instanceof HTMLElement)) return
    const delta = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top
    const next =
      scroller.scrollTop + delta - (scroller.clientHeight - target.getBoundingClientRect().height) / 2
    scroller.scrollTo({ top: Math.max(0, next), behavior })
  }

  useEffect(() => {
    return () => {
      if (settleTimer.current) window.clearTimeout(settleTimer.current)
      if (washTimer.current) window.clearTimeout(washTimer.current)
    }
  }, [])

  useLayoutEffect(() => {
    if (!holdScroll.current) return
    const node = scrollerRef.current
    if (node) node.scrollTop = heldTop.current
  })

  useEffect(() => {
    const scroller = scrollerRef.current
    return () => {
      if (!userScrolledReview.current && !holdScroll.current) return
      const top = scroller?.scrollTop ?? heldTop.current
      window.dispatchEvent(new CustomEvent('review-scroll-restore', { detail: { scrollTop: top } }))
    }
  }, [])

  useEffect(() => {
    if (freezeScroll.current) return
    holdScroll.current = false
    const frame = window.requestAnimationFrame(() => scrollToActive('smooth'))
    const follow = window.setTimeout(() => {
      if (!freezeScroll.current) scrollToActive('smooth')
    }, 220)
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(follow)
    }
    // scrollNonce is the file-row click. hunkCursor is prev/next. Accept must not scroll.
  }, [scrollNonce, hunkCursor])

  useLayoutEffect(() => {
    const pin = scrollPin.current
    const scroller = scrollerRef.current
    if (!pin || !scroller) return
    const apply = () => {
      const current = scrollPin.current
      const node = scrollerRef.current
      if (!current || !node) return
      const anchor =
        node.querySelector(`[data-review-anchor="${current.anchorId}"] [contenteditable="true"]`) ||
        node.querySelector(`[data-review-anchor="${current.anchorId}"]`)
      if (anchor instanceof HTMLElement) {
        const top = anchor.getBoundingClientRect().top - node.getBoundingClientRect().top
        const delta = top - current.anchorTop
        if (Math.abs(delta) > 0.5) node.scrollTop += delta
        return
      }
      node.scrollTop = current.scrollTop
    }
    apply()
    const frame = window.requestAnimationFrame(apply)
    const follow = window.setTimeout(() => {
      apply()
      scrollPin.current = null
    }, 40)
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(follow)
    }
  })

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || pending.length === 0) {
      setAway(null)
      setNextChangeEdge(null)
      return
    }
    const targets = pending
      .map((id) => scroller.querySelector(`[data-diff-hunk="${id}"][data-diff-scroll-target="true"]`))
      .filter((node): node is HTMLElement => node instanceof HTMLElement)
    if (targets.length === 0) return
    const read = () => {
      const root = scroller.getBoundingClientRect()
      const rows = targets.map((node, index) => {
        const rect = node.getBoundingClientRect()
        const visible = rect.bottom > root.top + 12 && rect.top < root.bottom - 12
        return { index, visible, edge: rect.top < root.top ? 'above' as const : 'below' as const }
      })
      const current = rows[Math.min(cursor, rows.length - 1)]
      if (current && !current.visible) {
        setAway(current.edge)
        setNextChangeEdge(null)
        return
      }
      setAway(null)
      const other = rows.find((row) => row.index !== current?.index && !row.visible)
      setNextChangeEdge(other ? other.edge : null)
      if (other) setNextChangeIndex(other.index)
    }
    read()
    const observer = new IntersectionObserver(read, { root: scroller, threshold: [0, 0.35, 1] })
    for (const node of targets) observer.observe(node)
    scroller.addEventListener('scroll', read, { passive: true })
    return () => {
      observer.disconnect()
      scroller.removeEventListener('scroll', read)
    }
  }, [activeHunk, cursor, pending, decisions])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const place = () => placeReviewChips(scroller)
    place()
    const observer = new ResizeObserver(place)
    observer.observe(scroller)
    scroller.addEventListener('scroll', place, { passive: true })
    return () => {
      observer.disconnect()
      scroller.removeEventListener('scroll', place)
    }
  }, [decisions, original, proposed])

  const pinScroll = (hunkId?: string) => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const nodes = hunkId ? [...scroller.querySelectorAll(`[data-diff-hunk="${hunkId}"]`)] : []
    const focused = document.activeElement
    const focusedNode =
      focused instanceof HTMLElement && nodes.some((node) => node.contains(focused)) ? focused : null
    const last = nodes[nodes.length - 1]
    const fallback =
      last instanceof HTMLElement ? (last.querySelector('[contenteditable="true"]') ?? last) : null
    const anchor = focusedNode ?? (fallback instanceof HTMLElement ? fallback : null)
    scrollPin.current = {
      anchorId: hunkId ?? '',
      anchorTop: anchor ? anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top : 0,
      scrollTop: scroller.scrollTop,
    }
  }

  const review = (kind: 'accepted' | 'rejected', hunkId?: string) => {
    if (locked) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const scroller = scrollerRef.current
    heldTop.current = scroller?.scrollTop ?? heldTop.current
    holdScroll.current = true
    if (scroller) scroller.scrollTo({ top: scroller.scrollTop, behavior: 'auto' })
    freezeScroll.current = true
    const previous = { ...decisions }
    setSettling({ hunkId, kind })
    if (settleTimer.current) window.clearTimeout(settleTimer.current)
    settleTimer.current = window.setTimeout(() => {
      pinScroll(hunkId)
      const nextDecisions: Record<string, HunkDecision> = hunkId
        ? { ...previous, [hunkId]: kind }
        : Object.fromEntries(hunks.map((id) => [id, kind]))
      const kept = blocks
        .filter((block) => {
          if (!block.hunkId) return false
          if (hunkId && block.hunkId !== hunkId) return false
          return kind === 'accepted' ? block.kind === 'add' : block.kind === 'remove'
        })
        .map((block) => block.id)
      setDecisions(nextDecisions)
      setWashOut(kept)
      setSettling(null)
      if (washTimer.current) window.clearTimeout(washTimer.current)
      washTimer.current = window.setTimeout(() => {
        scrollPin.current = null
        freezeScroll.current = false
        const saved = savedMarkdown(blocks, nextDecisions, overrides.current)
        const nextProposed = remainingProposal(blocks, nextDecisions, overrides.current)
        const stillPending = hunks.filter((id) => (nextDecisions[id] ?? 'pending') === 'pending').length
        setSaving(true)
        void onDecide({ kind, saved, proposed: nextProposed, pending: stillPending }).then((ok) => {
          setSaving(false)
          if (!ok) {
            setDecisions(previous)
            setWashOut([])
            holdScroll.current = false
            return
          }
          const node = scrollerRef.current
          if (node) node.scrollTop = heldTop.current
          window.setTimeout(() => {
            const later = scrollerRef.current
            if (holdScroll.current && later) later.scrollTop = heldTop.current
          }, 280)
        })
      }, reduceMotion ? 0 : WASH_OUT_MS)
    }, reduceMotion ? 0 : SETTLE_MS)
  }

  const beginEdit = (hunkId: string) => setEditingHunk(hunkId)

  const endEdit = (hunkId: string) => {
    window.setTimeout(() => {
      const active = document.activeElement
      const nodes = scrollerRef.current?.querySelectorAll(`[data-diff-hunk="${hunkId}"]`)
      const stillInside = active instanceof Node && [...(nodes ?? [])].some((node) => node.contains(active))
      if (stillInside) return
      setEditingHunk((current) => (current === hunkId ? null : current))
    }, 0)
  }

  const goToHunk = (delta: number) => {
    if (pending.length < 2) return
    holdScroll.current = false
    setHunkCursor((cursor + delta + pending.length) % pending.length)
  }

  const status = editingHunk
    ? 'Editing'
    : pending.length === 0
      ? 'Nothing waiting'
      : pending.length === 1
        ? 'One edit'
        : `${cursor + 1} of ${pending.length}`

  return (
    <div
      className="absolute inset-0 z-30 flex min-h-0 flex-col bg-background"
      data-testid={testIds.documentEditReview.root}
      aria-label={`Review ${title}`}
    >
      <div className="flex items-center gap-2 border-b border-primary/25 bg-primary/5 px-4 py-1.5">
        <span
          className="rounded-sm bg-primary/15 px-1.5 py-0.5 text-[11px] font-medium text-primary"
          data-testid={testIds.documentEditReview.title}
        >
          Review
        </span>
        {pending.length > 1 ? (
          <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-primary/10 hover:text-foreground disabled:opacity-40"
              aria-label="Previous edit"
              data-testid={testIds.documentEditReview.prev}
              disabled={locked}
              onClick={() => goToHunk(-1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <span className="tabular-nums">
              {cursor + 1} of {pending.length}
            </span>
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-primary/10 hover:text-foreground disabled:opacity-40"
              aria-label="Next edit"
              data-testid={testIds.documentEditReview.next}
              disabled={locked}
              onClick={() => goToHunk(1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </span>
        ) : null}
        {hunks.length > 0 ? (
          <div
            className="ml-auto flex h-6 items-stretch overflow-hidden rounded-md border border-border/80 bg-background text-[12px] leading-none"
            role="group"
            aria-label="Review the whole note"
          >
            <button
              type="button"
              disabled={locked}
              className="px-2 font-medium text-primary transition-colors duration-150 hover:bg-primary/10 disabled:opacity-40"
              data-testid={testIds.documentEditReview.acceptAll}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => review('accepted')}
            >
              Accept all
            </button>
            <span className="w-px self-stretch bg-border/80" aria-hidden />
            <button
              type="button"
              disabled={locked}
              className="px-2 text-muted-foreground transition-colors duration-150 hover:bg-muted/70 hover:text-foreground disabled:opacity-40"
              data-testid={testIds.documentEditReview.rejectAll}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => review('rejected')}
            >
              Reject all
            </button>
          </div>
        ) : null}
        <button
          type="button"
          className={[
            'rounded-md p-1 text-muted-foreground hover:bg-muted/70 hover:text-foreground',
            hunks.length === 0 ? 'ml-auto' : '',
          ].join(' ')}
          aria-label="Close review"
          data-testid={testIds.documentEditReview.close}
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div
        ref={scrollerRef}
        className="relative min-h-0 flex-1 overflow-y-auto bg-background [overflow-anchor:none]"
        data-testid={testIds.documentEditReview.reader}
        onScroll={(event) => {
          heldTop.current = event.currentTarget.scrollTop
          userScrolledReview.current = true
        }}
      >
        <article className="document-reading-prose mx-auto box-border w-full max-w-[42rem] px-10 py-8">
        {chunkBlocks(blocks).map((chunk) => {
          const decision = chunk.hunkId ? (decisions[chunk.hunkId] ?? 'pending') : 'pending'
          if (chunk.hunkId && decision === 'accepted' && chunk.blocks.every((block) => block.kind === 'remove')) {
            return null
          }
          if (chunk.hunkId && decision === 'rejected' && chunk.blocks.every((block) => block.kind === 'add')) {
            return null
          }
          const visible = chunk.blocks.filter((block) => {
            if (!chunk.hunkId) return true
            if (decision === 'accepted' && block.kind === 'remove') return false
            if (decision === 'rejected' && block.kind === 'add') return false
            return true
          })
          return (
            <div
              key={chunk.id}
              className={chunk.hunkId ? 'group/hunk relative' : undefined}
              data-diff-hunk={chunk.hunkId}
              data-testid={chunk.hunkId ? testIds.documentEditReview.hunk(chunk.hunkId) : undefined}
            >
              {visible.map((block, index) => {
                const leaving =
                  settling &&
                  block.hunkId &&
                  (!settling.hunkId || block.hunkId === settling.hunkId) &&
                  (settling.kind === 'accepted' ? block.kind === 'remove' : block.kind === 'add')
                const wash = washOut.includes(block.id)
                const editable = block.kind === 'add' && decision === 'pending' && !locked
                const changed = block.kind === 'add' || block.kind === 'remove'
                return (
                  <div
                    key={block.id}
                    data-diff-hunk={block.hunkId}
                    data-diff-scroll-target={
                      block.hunkId === activeHunk &&
                      (block.kind === 'add' || !visible.some((item) => item.kind === 'add'))
                        ? 'true'
                        : undefined
                    }
                    data-review-anchor={wash ? block.hunkId : undefined}
                    className={['relative', index > 0 ? 'mt-[0.75em]' : '', leaving ? 'diff-review-leave' : ''].join(' ')}
                  >
                    {changed ? (
                      <span
                        className={[
                          'diff-review-gutter select-none text-[11px] font-medium opacity-0 transition-opacity duration-200 group-hover/hunk:opacity-100',
                          block.kind === 'add'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400',
                        ].join(' ')}
                        data-testid={
                          block.hunkId
                            ? testIds.documentEditReview.hunkGutter(
                                block.hunkId,
                                block.kind === 'add' ? 'add' : 'remove',
                              )
                            : undefined
                        }
                        aria-hidden
                      >
                        {block.kind === 'add' ? '+' : '-'}
                      </span>
                    ) : null}
                    <div
                      data-diff-kind={changed ? block.kind : undefined}
                      data-testid={
                        block.hunkId && (block.kind === 'add' || block.kind === 'remove')
                          ? testIds.documentEditReview.hunkMark(block.hunkId, block.kind)
                          : undefined
                      }
                      className={[
                        changed || wash ? 'diff-review-mark' : '',
                        block.kind === 'add'
                          ? 'cursor-text border-green-700/40 bg-green-600/[0.16] hover:border-green-700/55 hover:bg-green-600/[0.22] dark:border-green-400/75 dark:bg-green-500/[0.24] dark:hover:border-green-300 dark:hover:bg-green-500/[0.32]'
                          : '',
                        block.kind === 'remove'
                          ? 'border-red-700/35 bg-red-600/[0.12] text-red-950/90 line-through decoration-red-700/45 hover:border-red-700/50 hover:bg-red-600/[0.18] dark:border-red-400/70 dark:bg-red-500/20 dark:text-red-100/90 dark:decoration-red-400/60 dark:hover:border-red-300 dark:hover:bg-red-500/[0.28]'
                          : '',
                        wash && block.kind === 'add' ? 'diff-review-wash-out' : '',
                        wash && block.kind === 'remove' ? 'diff-review-wash-out-restore' : '',
                      ].join(' ')}
                    >
                    <BlockBody
                      block={block}
                      editable={editable}
                      onFocus={block.hunkId ? () => beginEdit(block.hunkId!) : undefined}
                      onBlur={block.hunkId ? () => endEdit(block.hunkId!) : undefined}
                      onInput={(markdown) => {
                        overrides.current[block.id] = markdown
                      }}
                    />
                    </div>
                    {chunk.hunkId && decision === 'pending' && (block.kind === 'add' || block.kind === 'remove') ? (
                      <div
                        className="diff-review-hunk-actions"
                        data-testid={testIds.documentEditReview.hunkActions(
                          chunk.hunkId,
                          block.kind === 'add' ? 'add' : 'remove',
                        )}
                      >
                        <button
                          type="button"
                          disabled={locked}
                          className={[
                            'inline-flex h-6 items-center gap-1 rounded-md border px-2 text-[12px] leading-none disabled:opacity-40',
                            block.kind === 'add'
                              ? 'diff-review-accept'
                              : 'border-border/80 bg-background text-muted-foreground hover:bg-muted/70 hover:text-foreground',
                          ].join(' ')}
                          data-testid={
                            block.kind === 'add'
                              ? testIds.documentEditReview.hunkAccept(chunk.hunkId)
                              : testIds.documentEditReview.hunkReject(chunk.hunkId)
                          }
                          data-review-hover={block.kind === 'add' ? 'accept' : 'reject'}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => review(block.kind === 'add' ? 'accepted' : 'rejected', chunk.hunkId)}
                        >
                          {block.kind === 'add' ? <Check className="h-3 w-3" /> : null}
                          {block.kind === 'add' ? 'Accept' : 'Reject'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )
        })}
        </article>
        {away || nextChangeEdge ? (
          <button
            type="button"
            className={[
              'sticky left-1/2 z-10 flex w-fit -translate-x-1/2 items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-xs text-foreground shadow-sm',
              (away ?? nextChangeEdge) === 'above' ? 'top-3' : 'bottom-3',
            ].join(' ')}
            onClick={() => {
              if (away) scrollToActive('smooth')
              else {
                holdScroll.current = false
                setHunkCursor(nextChangeIndex)
              }
            }}
          >
            {(away ?? nextChangeEdge) === 'above' ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            {away ? (away === 'above' ? 'Edit above' : 'Edit below') : 'Next change'}
          </button>
        ) : null}
      </div>
      {editingHunk ? (
        <div
          className="border-t border-border px-4 py-2 text-xs text-foreground"
          data-testid={testIds.documentEditReview.status}
        >
          Editing
        </div>
      ) : (
        <div className="sr-only" data-testid={testIds.documentEditReview.status}>
          {status}
        </div>
      )}
    </div>
  )
}
